import React, { createContext, useCallback, useContext, useState } from 'react';
import { CheckCircle2, AlertCircle, X } from 'lucide-react';

type ToastTone = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  message: string;
  tone: ToastTone;
}

interface ToastContextValue {
  show: (message: string, tone?: ToastTone) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const show = useCallback((message: string, tone: ToastTone = 'info') => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, tone }]);
    window.setTimeout(
      () => setToasts((prev) => prev.filter((t) => t.id !== id)),
      4500
    );
  }, []);

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      <div className="fixed top-4 right-4 z-[60] flex flex-col gap-2 w-[min(360px,calc(100vw-2rem))]">
        {toasts.map((t) => (
          <ToastItem
            key={t.id}
            toast={t}
            onClose={() => setToasts((prev) => prev.filter((p) => p.id !== t.id))}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

const ToastItem: React.FC<{ toast: Toast; onClose: () => void }> = ({
  toast,
  onClose,
}) => {
  const tone =
    toast.tone === 'success'
      ? 'border-sage-200 bg-white'
      : toast.tone === 'error'
      ? 'border-red-200 bg-white'
      : 'border-sand-200 bg-white';

  const Icon =
    toast.tone === 'error' ? AlertCircle : toast.tone === 'success' ? CheckCircle2 : AlertCircle;

  const iconColor =
    toast.tone === 'success'
      ? 'text-sage-600'
      : toast.tone === 'error'
      ? 'text-red-600'
      : 'text-sand-500';

  return (
    <div
      className={[
        'pointer-events-auto rounded-xl border shadow-soft px-3.5 py-3 flex items-start gap-3 animate-slide-in-right',
        tone,
      ].join(' ')}
    >
      <Icon className={['h-4 w-4 mt-0.5 shrink-0', iconColor].join(' ')} />
      <p className="text-sm text-sand-800 flex-1">{toast.message}</p>
      <button
        onClick={onClose}
        aria-label="Dismiss"
        className="text-sand-400 hover:text-sand-700 transition-colors"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};
